import { Common } from './common.model';
import { Embedded, EmbeddedCell, EmbeddedCompany, EmbeddedTribe, EmbeddedUser, toCellEmbedded, toCompanyEmbedded, toTribeEmbedded, toUserEmbedded } from './embed.model';

export type embed = keyof EmbedProperties;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $cell: EmbeddedCell;
    $tribe: EmbeddedTribe;
    $company: EmbeddedCompany;
}

export type EmbedUser<T extends embed> = User & Partial<Pick<EmbedProperties, T>>;

export const configuration: { [K in embed]: { embed: string; transform: (user: User, embedded: Embedded) => EmbedProperties[K] } } = {
    $createdBy: {
        embed: 'users',
        transform: (data: User, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: User, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: User, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $cell: {
        embed: 'cells',
        transform: (data: User, embedded: Embedded): EmbeddedCell => toCellEmbedded(data, 'cell', embedded),
    },
    $tribe: {
        embed: 'tribes',
        transform: (data: User, embedded: Embedded): EmbeddedTribe => toTribeEmbedded(data, 'tribe', embedded),
    },
    $company: {
        embed: 'companies',
        transform: (data: User, embedded: Embedded): EmbeddedCompany => toCompanyEmbedded(data, 'company', embedded),
    },
};

export class UserHttp extends Common {
    declare public id: string;
    public name: string;
    public firstName: string;
    public lastName: string;
    public email: string;
    public mailLink: string;
    public avatar: string;
    public jobTitle: string;
    public company: string;
    public workPhone: string;
    public mobilePhone: string;
    public companyPhone: string;
    public language: string;
    public isInternal: boolean;
    public isBot: boolean;
    public is247: boolean;
    public isHdsContact: boolean;
    public authenticationScheme: string;
    public userMetadata?: { [key: string]: string };
    public tribe?: string;
    public cell?: string;

    constructor(data: Partial<UserHttp>) {
        super(data);
        this.id = data.id;
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.email = data.email;
        this.mailLink = data.mailLink;
        this.avatar = data.avatar;
        this.jobTitle = data.jobTitle;
        this.company = data.company;
        this.workPhone = data.workPhone;
        this.mobilePhone = data.mobilePhone;
        this.companyPhone = data.companyPhone;
        this.language = data.language;
        this.isInternal = data.isInternal;
        this.isBot = data.isBot;
        this.is247 = data.is247;
        this.isHdsContact = data.isHdsContact;
        this.authenticationScheme = data.authenticationScheme;
        this.userMetadata = data.userMetadata;
        this.tribe = data.tribe;
        this.cell = data.cell;
    }
}

export class User extends UserHttp {
    public blockedAt?: Date;
    public blockedDetail?: string;
    public fullName: string;

    constructor(data: Partial<User>) {
        super(data);
        this.blockedAt = data.blockedAt ? new Date(data.blockedAt) : undefined;
        this.blockedDetail = data.blockedDetail ?? undefined;
        this.fullName = `${data.firstName} ${data.lastName}`;
    }

    public static toUserEmbedded<T extends embed>(data: User, embedded: Embedded, customFields: T[]): EmbedUser<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});

        // TODO
        // hacky
        // metadata has to be convert to map...
        return { ...new User(data), ...customData } as EmbedUser<T>;
    }

    public static toUsersEmbedded<T extends embed>(data: User[], embedded: Embedded, customFields: T[]): EmbedUser<T>[] {
        return data.map((user) => {
            const customData = customFields.reduce((acc, field: embed) => {
                acc[field] = configuration[field].transform(user, embedded);
                return acc;
            }, {});
            return { ...new User(user), ...customData } as EmbedUser<T>;
        });
    }
}
